import {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {generateQrBlob, type QrLogoState, type QrStyleState} from '../../../common/utils/qrRenderer'
import {QR_DOWNLOAD_FORMATS, type QrDownloadFormat} from '../../../common/constants/qrDownloadFormats'
import {downloadFile} from '../../../common/utils/downloadFile'
import {useMe} from '../../../common/hooks/useMe'
import {trackEvent} from '../../../common/analytics/trackEvent'
import QrExportDialog from './QrExportDialog'

interface QrDownloadPanelProps {
  data: string
  style: QrStyleState
  logo: QrLogoState
}

const QrDownloadPanel = ({data, style, logo}: QrDownloadPanelProps) => {
  const {t} = useTranslation()
  const {me} = useMe()
  const [busyExt, setBusyExt] = useState<QrDownloadFormat['extension'] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogFormat, setDialogFormat] = useState<QrDownloadFormat | null>(null)

  // Signed-in users with at least one credit get the direct-download fast path —
  // same gate the icon generator uses; the upsell dialog only targets visitors
  // who haven't paid, even though generating a QR code itself is free.
  const hasCredits = me !== null && me.credits > 0
  const disabled = data.trim().length === 0

  const handleDownload = useCallback(
    async (format: QrDownloadFormat) => {
      if (disabled || busyExt) return
      trackEvent('qr_download_clicked', {format: format.extension, has_credits: hasCredits})
      if (!hasCredits) {
        setDialogFormat(format)
        return
      }
      setError(null)
      setBusyExt(format.extension)
      try {
        const blob = await generateQrBlob(data, style, logo, format.extension)
        await downloadFile(blob, `qr-code.${format.fileExt}`)
        trackEvent('qr_download_completed', {format: format.extension, size_bytes: blob.size, gated: false})
      } catch {
        setError(t('qrGenerator.download.failed'))
      } finally {
        setBusyExt(null)
      }
    },
    [data, style, logo, disabled, busyExt, hasCredits, t],
  )

  const handleDialogClose = useCallback(() => setDialogFormat(null), [])

  return (
    <div className="qrg-download">
      <div className="qrg-download-grid">
        {QR_DOWNLOAD_FORMATS.map((format) => (
          <button
            key={format.extension}
            type="button"
            className="qrg-btn qrg-btn-secondary"
            disabled={disabled || busyExt !== null}
            onClick={() => handleDownload(format)}
          >
            {busyExt === format.extension ? t('qrGenerator.download.busy') : format.label}
          </button>
        ))}
      </div>
      {error && <p className="qrg-error">{error}</p>}
      {disabled && <p className="qrg-hint">{t('qrGenerator.download.needData')}</p>}

      {dialogFormat && (
        <QrExportDialog data={data} style={style} logo={logo} format={dialogFormat} onClose={handleDialogClose} />
      )}
    </div>
  )
}

export default QrDownloadPanel
